// redux/Slices/servicesSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../Url/BaseUrl';

// ---------- Async Thunks ----------

// Fetch master data (Operators, States, Zones)
export const fetchMasterData = createAsyncThunk(
  'services/fetchMasterData',
  async (_, { rejectWithValue }) => {
    try {
      const [operatorsRes, statesRes, zonesRes] = await Promise.all([
        axios.get(`${BaseUrl.baseurl}BusOperators`, { headers: { accept: 'text/plain' } }),
        axios.get(`${BaseUrl.baseurl}States`, { headers: { accept: 'text/plain' } }),
        axios.get(`${BaseUrl.baseurl}Zones`, { headers: { accept: 'text/plain' } }),
      ]);
      return {
        operators: operatorsRes.data,
        states: statesRes.data,
        zones: zonesRes.data,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Search buses
export const searchBuses = createAsyncThunk(
  'services/searchBuses',
  async (searchParams, { rejectWithValue }) => {
    try {
      const payload = {};
      Object.keys(searchParams).forEach(key => {
        if (searchParams[key]?.trim()) {
          payload[key] = searchParams[key].trim();
        }
      });
      const response = await axios.post(`${BaseUrl.baseurl}Buses/BusSearchResults`, payload, {
        headers: { 'Content-Type': 'application/json', accept: 'text/plain' }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Deactivate bus (PUT with status: false)
export const deactivateBus = createAsyncThunk(
  'services/deactivateBus',
  async (bus, { rejectWithValue, getState }) => {
    try {
      // Get arrivalAndDeparture from state (or fall back to empty array)
      const state = getState();
      const busStands = state.services.arrivalAndDeparture || [];

      // Build the payload matching the API schema
      const payload = {
        bus: {
          ...bus,
          status: false,
        },
        busStands: busStands,
      };

      await axios.put(`${BaseUrl.baseurl}Buses`, payload, {
        headers: { 'Content-Type': 'application/json', accept: 'text/plain' }
      });
      return bus.busNo;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Activate bus (GET ApprovalStatus)
export const activateBus = createAsyncThunk(
  'services/activateBus',
  async (busNo, { rejectWithValue }) => {
    try {
      await axios.get(`${BaseUrl.baseurl}Buses/ApprovalStatus?Id=${busNo}`, {
        headers: { accept: '*/*' }
      });
      return busNo;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch travel history for a bus
export const fetchTravelHistory = createAsyncThunk(
  'services/fetchTravelHistory',
  async (busNo, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl.baseurl}Buses/${busNo}/travel-history`);
      return { busNo, history: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ---------- Initial State ----------
const initialState = {
  operators: [],
  states: [],
  zones: [],
  masterDataLoading: true,
  masterDataError: null,
  searchParams: {
    operatorName: '',
    operatorNumber: '',
    stateName: '',
    stateCode: '',
    zoneName: '',
    zoneCode: '',
  },
  services: [],
  arrivalAndDeparture: [],
  loading: false,
  searchPerformed: false,
  searchError: null,
  selectedService: null,
  showDetailsModal: false,
  showTravelHistoryModal: false,
  travelHistory: [],
  travelHistoryLoading: false,
  activatingId: null,
  deactivatingId: null,
  shouldRefreshAfterEdit: false,
  lastSearchParams: null,
};

// ---------- Slice ----------
const servicesSlice = createSlice({
  name: 'services',
  initialState,
  reducers: {
    updateSearchParam: (state, action) => {
      const { name, value } = action.payload;
      state.searchParams[name] = value;
    },
    setOperatorBySelection: (state, action) => {
      const { operatorName, operatorNumber } = action.payload;
      state.searchParams.operatorName = operatorName;
      state.searchParams.operatorNumber = operatorNumber;
    },
    setStateBySelection: (state, action) => {
      const { stateName, stateCode } = action.payload;
      state.searchParams.stateName = stateName;
      state.searchParams.stateCode = stateCode;
      // Reset zone when state changes
      state.searchParams.zoneName = '';
      state.searchParams.zoneCode = '';
    },
    setZoneBySelection: (state, action) => {
      const { zoneName, zoneCode } = action.payload;
      state.searchParams.zoneName = zoneName;
      state.searchParams.zoneCode = zoneCode;
    },
    clearAllFilters: (state) => {
      state.searchParams = initialState.searchParams;
      state.services = [];
      state.arrivalAndDeparture = [];
      state.searchPerformed = false;
      state.searchError = null;
    },
    setSelectedService: (state, action) => {
      state.selectedService = action.payload;
    },
    setShowDetailsModal: (state, action) => {
      state.showDetailsModal = action.payload;
    },
    setShowTravelHistoryModal: (state, action) => {
      state.showTravelHistoryModal = action.payload;
    },
    clearTravelHistory: (state) => {
      state.travelHistory = [];
    },
    clearArrivalDeparture: (state) => {
      state.arrivalAndDeparture = [];
    },
    setShouldRefreshAfterEdit: (state, action) => {
      state.shouldRefreshAfterEdit = action.payload;
    },
    refreshCurrentSearch: (state) => {
      state.shouldRefreshAfterEdit = true;
    },
    clearRefreshFlag: (state) => {
      state.shouldRefreshAfterEdit = false;
    },
    storeLastSearchParams: (state) => {
      state.lastSearchParams = { ...state.searchParams };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMasterData.pending, (state) => {
        state.masterDataLoading = true;
        state.masterDataError = null;
      })
      .addCase(fetchMasterData.fulfilled, (state, action) => {
        state.masterDataLoading = false;
        state.operators = action.payload.operators;
        state.states = action.payload.states;
        state.zones = action.payload.zones;
      })
      .addCase(fetchMasterData.rejected, (state, action) => {
        state.masterDataLoading = false;
        state.masterDataError = action.payload;
      })
      .addCase(searchBuses.pending, (state) => {
        state.loading = true;
        state.searchError = null;
      })
      .addCase(searchBuses.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload?.buses || [];
        state.arrivalAndDeparture = action.payload?.arrivalAndDeparture || [];
        state.searchPerformed = true;
        state.shouldRefreshAfterEdit = false;
        state.lastSearchParams = { ...state.searchParams };
      })
      .addCase(searchBuses.rejected, (state, action) => {
        state.loading = false;
        state.searchError = action.payload;
        state.services = [];
        state.arrivalAndDeparture = [];
        state.searchPerformed = true;
      })
      .addCase(deactivateBus.pending, (state, action) => {
        state.deactivatingId = action.meta.arg.busNo;
      })
      .addCase(deactivateBus.fulfilled, (state, action) => {
        state.deactivatingId = null;
        const busNo = action.payload;
        const bus = state.services.find(s => s.busNo === busNo);
        if (bus) bus.status = false;
        if (state.selectedService?.busNo === busNo) state.selectedService.status = false;
      })
      .addCase(deactivateBus.rejected, (state) => {
        state.deactivatingId = null;
      })
      .addCase(activateBus.pending, (state, action) => {
        state.activatingId = action.meta.arg;
      })
      .addCase(activateBus.fulfilled, (state, action) => {
        state.activatingId = null;
        const busNo = action.payload;
        const bus = state.services.find(s => s.busNo === busNo);
        if (bus) bus.status = true;
        if (state.selectedService?.busNo === busNo) state.selectedService.status = true;
      })
      .addCase(activateBus.rejected, (state) => {
        state.activatingId = null;
      })
      .addCase(fetchTravelHistory.pending, (state) => {
        state.travelHistoryLoading = true;
      })
      .addCase(fetchTravelHistory.fulfilled, (state, action) => {
        state.travelHistoryLoading = false;
        state.travelHistory = action.payload.history;
      })
      .addCase(fetchTravelHistory.rejected, (state) => {
        state.travelHistoryLoading = false;
        state.travelHistory = [];
      });
  },
});

export const {
  updateSearchParam,
  setOperatorBySelection,
  setStateBySelection,
  setZoneBySelection,
  clearAllFilters,
  setSelectedService,
  setShowDetailsModal,
  setShowTravelHistoryModal,
  clearTravelHistory,
  clearArrivalDeparture,
  setShouldRefreshAfterEdit,
  refreshCurrentSearch,
  clearRefreshFlag,
  storeLastSearchParams,
} = servicesSlice.actions;

export default servicesSlice.reducer;